import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import NotificationBell from '@/components/notifications/NotificationBell';
import { ROUTES } from '@/config/routes';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store';
import { Role, getInitials } from '@shared';
import { Building2, CircleDot, LayoutDashboard, LogOut, Menu, Users, X } from 'lucide-react';

const navLinks: (
  | {
      label: string;
      to: string;
      children?: {
        label: string;
        to: string;
      }[];
    }
  | {
      label: string;
      to: string;
      children?: undefined;
    }
)[] = [
  { label: 'Fonctionnalités', to: ROUTES.FONCTIONNALITES },
  { label: 'Tarifs', to: ROUTES.TARIFS },
  {
    label: 'Concours',
    to: '/concours',
  },
  {
    label: 'Clubs',
    to: '/clubs',
  },
  { label: 'Organisateurs', to: '/organisateurs' },
  { label: 'Contact', to: ROUTES.CONTACT },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const logout = useAuthStore(s => s.logout);
  const openRegister = useSaasStore(s => s.openRegister);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | undefined>(undefined);

  // Close dropdown on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  function handleLogout() {
    setUserMenuOpen(false);
    logout();
    navigate(ROUTES.HOME);
  }

  return (
    <nav className='fixed top-0 z-50 w-full border-b border-slate-200/50 glass dark:border-slate-700/50'>
      <div className='mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6'>
        <Link to={ROUTES.HOME} className='flex items-center gap-2'>
          <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 shadow-lg shadow-blue-200 dark:shadow-blue-900/50'>
            <CircleDot className='h-6 w-6 text-white' />
          </div>
          <span className='text-xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100'>
            <span className='lg:hidden'>
              B<span className='text-blue-600 dark:text-blue-400'>C</span>
            </span>
            <span className='hidden lg:inline-block'>
              Boules<span className='text-blue-600 dark:text-blue-400'>Connect</span>
            </span>
          </span>
        </Link>

        <ul className='hidden items-center gap-6 text-sm font-semibold text-slate-600 lg:flex dark:text-slate-300'>
          {navLinks.map(link => (
            <li key={link.label} className={link.children ? 'group relative' : ''}>
              <Link
                to={link.to}
                className={
                  location.pathname === link.to || (link.children && location.pathname.startsWith(link.to))
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'transition-colors hover:text-blue-600 dark:hover:text-blue-400'
                }
              >
                {link.label}
              </Link>
              {link.children && (
                <div className='absolute left-0 z-50 hidden pt-2 group-hover:flex'>
                  <div className='w-56 overflow-hidden rounded-md border border-slate-200 bg-white opacity-0 shadow-lg transition-opacity duration-300 group-hover:opacity-100 dark:border-slate-700 dark:bg-slate-800'>
                    {link.children.map(child => (
                      <Link
                        key={child.label}
                        to={child.to}
                        className='block px-4 py-2 text-slate-800 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>

        <div className='flex items-center gap-2 sm:gap-3'>
          {isAuthenticated && user ? (
            <>
              <NotificationBell />

              {/* ─── User dropdown ─────────────────────────── */}
              <div ref={userMenuRef as React.RefObject<HTMLDivElement>} className='relative'>
                <button
                  type='button'
                  onClick={() => setUserMenuOpen(prev => !prev)}
                  className='flex cursor-pointer items-center gap-2 rounded-full border border-blue-200 bg-blue-50 py-1.5 pl-1.5 pr-4 text-sm font-bold text-blue-700 transition hover:bg-blue-100 dark:border-blue-700/50 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50'
                >
                  <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white'>
                    {getInitials(`${user.firstName ?? ''} ${user.lastName ?? ''}`)}
                  </div>
                  <span className='hidden max-w-30 truncate sm:block'>{user.firstName}</span>
                </button>

                {userMenuOpen && (
                  <div className='absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800'>
                    {/* Header — nom + email */}
                    <div className='border-b border-slate-100 px-4 py-3 dark:border-slate-700'>
                      <p className='text-sm font-semibold text-slate-800 dark:text-slate-100'>
                        {user.firstName} {user.lastName}
                      </p>
                      {user.email && (
                        <p className='mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400'>{user.email}</p>
                      )}
                    </div>

                    {/* Menu items */}
                    <div className='py-1'>
                      <button
                        type='button'
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate(ROUTES.ROLE_LANDING);
                        }}
                        className='flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/50'
                      >
                        <LayoutDashboard className='h-4 w-4 text-slate-400 dark:text-slate-500' />
                        Mon espace
                      </button>
                      {(user.role === Role.MEMBER || user.role === Role.CLUB_ADMIN) && user?.club && (
                        <button
                          type='button'
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate(ROUTES.CLUB_PROFILE.replace(':id', String(user?.club?.id)));
                          }}
                          className='flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/50'
                        >
                          <Users className='h-4 w-4 text-slate-400 dark:text-slate-500' />
                          Mon club
                        </button>
                      )}
                      {user.role === Role.ORGANIZATION_ADMIN && user?.organizationsAdmin && (
                        <button
                          type='button'
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate(
                              ROUTES.ORGANIZATION_PROFILE.replace(
                                ':id',
                                String(user?.organizationsAdmin[0]?.organizationId),
                              ),
                            );
                          }}
                          className='flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/50'
                        >
                          <Building2 className='h-4 w-4 text-slate-400 dark:text-slate-500' />
                          Mon organisation
                        </button>
                      )}
                      <button
                        type='button'
                        onClick={handleLogout}
                        className='flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                      >
                        <LogOut className='h-4 w-4' />
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to={ROUTES.LOGIN}
                className='text-sm font-bold text-slate-700 transition hover:text-blue-600 dark:text-slate-200 dark:hover:text-blue-400'
              >
                Connexion
              </Link>
              {openRegister && (
                <Link
                  to={ROUTES.REGISTER}
                  className='hidden rounded-full bg-blue-600 px-5 py-2 text-sm font-bold text-white shadow-xl shadow-blue-100 transition-all hover:bg-blue-700 sm:block dark:shadow-blue-900/30'
                >
                  Inscription · Gratuit
                </Link>
              )}
            </>
          )}

          {/* Hamburger — visible below lg */}
          <button
            type='button'
            onClick={() => setMobileMenuOpen(prev => !prev)}
            className='flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 lg:hidden dark:text-slate-300 dark:hover:bg-slate-700'
            aria-label='Menu'
          >
            {mobileMenuOpen ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
          </button>
        </div>
      </div>

      {/* ─── Mobile menu ─────────────────────────── */}
      {mobileMenuOpen && (
        <div className='border-t border-slate-200/50 bg-white/95 backdrop-blur-md lg:hidden dark:border-slate-700/50 dark:bg-slate-900/95'>
          <ul className='flex flex-col gap-1 px-4 py-3 text-sm font-semibold text-slate-600 dark:text-slate-300'>
            {navLinks.map(link => (
              <li key={link.label}>
                <Link
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block rounded-lg px-3 py-2.5 transition ${
                    location.pathname === link.to
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          {!isAuthenticated && openRegister && (
            <div className='border-t border-slate-100 px-4 py-3 dark:border-slate-700/50'>
              <Link
                to={ROUTES.REGISTER}
                onClick={() => setMobileMenuOpen(false)}
                className='block w-full rounded-full bg-blue-600 py-2.5 text-center text-sm font-bold text-white shadow-lg transition hover:bg-blue-700'
              >
                Inscription · Gratuit
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
